/* istanbul ignore file */
import { getMesssageForPlayerErrorCode } from '@foundation-player/error-codes';
import type {
  AudioTrack,
  FoundationError as PublicFoundationError,
  FoundationEvent as PublicFoundationEvent,
  MetadataUpdatedEvent as PublicMetadataUpdatedEvent,
  TextTrack,
} from '@foundation-player/types';
import { AdvertisingEvent } from '../advertising/event-types';
import { PlayerEvent } from './events';
import { PlayerMetric } from './player-metric';

export type FoundationEvent = PublicFoundationEvent | PlayerMetric | PlayerEvent | AdvertisingEvent;
export type EventDispatcher = (event: FoundationEventBase) => void;

export interface FoundationEventBaseOptions<SourceType = unknown> {
  /**
   * The original event that was emitted, resulting in this event being emitted.
   *
   * @remarks
   * Most events are emitted by the root player and have a different type and payload than our events.
   * To provide a consistent experience, we convert these events into our own events and emit them.
   *
   * This should only be used for debugging purposes.
   *
   * @internal
   */
  originalEvent?: SourceType;
}

export class FoundationEventBase<SourceEvent = unknown, EventType extends string = FoundationEvent> extends Event {
  // event defines it as readonly string, and we want to make it more specific
  // we use ! here, because typescript, does not recognize that it's set via super()
  public type!: EventType;
  public originalEvent?: SourceEvent;

  constructor(type: EventType, opts: FoundationEventBaseOptions<SourceEvent> = {}) {
    super(type);
    this.originalEvent = opts.originalEvent;
  }
}

interface CueEventOptions<SourceEvent> extends FoundationEventBaseOptions<SourceEvent> {
  start?: number;
  end?: number;
  text?: string;
  cueId: string;
}

export class CueEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public start?: number;
  public end?: number;
  public text?: string;
  public subtitleId: string;
  public cueId: string;
  constructor(type: FoundationEvent, opts: CueEventOptions<EventType>) {
    super(type, opts);
    this.start = opts.start;
    this.end = opts.end;
    this.text = opts.text;
    this.subtitleId = opts.cueId;
    this.cueId = opts.cueId;
  }
}

interface VolumeChangedOptions<SourceEvent> extends FoundationEventBaseOptions<SourceEvent> {
  currentVolume: number;
}

export class VolumeChangedEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public currentVolume: number;

  constructor(type: FoundationEvent, opts: VolumeChangedOptions<EventType>) {
    super(type, opts);
    this.currentVolume = opts.currentVolume;
  }
}

interface TimeEventOptions<SourceEvent> extends FoundationEventBaseOptions<SourceEvent> {
  time: number;
  timestamp?: number;
}
export class TimeEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public time: number;
  public timestamp?: number;

  constructor(type: FoundationEvent, opts: TimeEventOptions<EventType>) {
    super(type, opts);
    this.time = opts.time;
    this.timestamp = opts.timestamp;
  }
}

interface ErrorOptions<EventType = unknown, ResponseType = unknown> extends FoundationEventBaseOptions<EventType> {
  code?: number;
  message?: string;
  response?: ResponseType; // parsed server error
  responseText?: string; // original server error
  name?: string;

  /**
   * Add additional meta data to assist with debugging
   */
  context?: Record<string, unknown>;
}

export class FoundationErrorEvent<SourceEvent = unknown, ResponseType = unknown>
  extends FoundationEventBase<SourceEvent, typeof PlayerEvent.Error | typeof PlayerEvent.SuppressedError>
  implements Omit<PublicFoundationError, 'type'>
{
  public code?: number;
  public message?: string;
  public response?: ResponseType; // parsed server error
  public responseText?: string; // original server error
  public name?: string;
  public context?: Record<string, unknown>;

  constructor(
    type: typeof PlayerEvent.Error | typeof PlayerEvent.SuppressedError,
    opts: ErrorOptions<SourceEvent, ResponseType>,
  ) {
    super(type, opts);
    this.code = opts.code;
    this.message = opts.message;
    this.response = opts.response;
    this.responseText = opts.responseText;
    this.name = opts.name;
    this.context = opts.context;

    if (isEntitlementResponse(this.response)) {
      this.code = this.response.errorCode;
      this.message = this.response.error;
    }

    if (this.code) {
      const message = getMesssageForPlayerErrorCode(this.code);
      if (message) {
        this.context = {
          message: this.message,
          ...opts.context,
        };
        this.message = message;
      }
    }
  }
}

interface DestroyOptions<EventType = unknown> extends FoundationEventBaseOptions<EventType> {
  cause: string | PublicFoundationError | FoundationErrorEvent;
}

export class FoundationDestroy<EventType = unknown> extends FoundationEventBase<EventType> {
  public cause: string;
  public error?: PublicFoundationError | FoundationErrorEvent;

  constructor(type: FoundationEvent, opts: DestroyOptions<EventType>) {
    super(type, opts);
    this.cause = typeof opts.cause === 'string' ? opts.cause : opts.cause.type;
    this.error = typeof opts.cause !== 'string' ? opts.cause : undefined;
  }
}

interface EntitlementResponse {
  error: string;
  errorCode: number;
  origin: string;
}

function isEntitlementResponse(response: unknown): response is EntitlementResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'origin' in response &&
    typeof response.origin === 'string' &&
    'errorCode' in response &&
    typeof Number(response.errorCode) === 'number' &&
    'error' in response &&
    typeof response.error === 'string'
  );
}

export enum MediaType {
  Video = 'video',
  Audio = 'audio',
}

interface MediaAdaption<EventType = unknown> extends FoundationEventBaseOptions<EventType> {
  representationId: string;
  mediaType: MediaType;
}

export class MediaAdaptionEvent<SourceEvent = unknown> extends FoundationEventBase<SourceEvent> {
  public representationId: string;
  public mediaType: MediaType;

  constructor(type: FoundationEvent, opts: MediaAdaption<SourceEvent>) {
    super(type, opts);
    this.representationId = opts.representationId;
    this.mediaType = opts.mediaType;
  }
}

interface SeekedOptions extends FoundationEventBaseOptions {
  startPosition: number;
  seekPosition: number;
}
export class Seeked extends FoundationEventBase {
  public startPosition: number;
  public seekPosition: number;

  constructor(type: FoundationEvent, opts: SeekedOptions) {
    super(type, opts);
    this.startPosition = opts.startPosition;
    this.seekPosition = opts.seekPosition;
  }
}

export class SessionStart extends FoundationEventBase {
  constructor(public id: string) {
    super(PlayerEvent.SessionStart);
  }
}

interface MediaLoadEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  mediaId?: string;
}

export class MediaLoadEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public mediaId?: string;

  constructor(type: FoundationEvent, opts: MediaLoadEventOptions<EventType>) {
    super(type, opts);
    this.mediaId = opts.mediaId;
  }
}

interface MetricEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  start: number;
  end: number;
  duration?: number;
}

export class MetricEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public start: number;
  public end: number;
  public duration: number;

  constructor(type: FoundationEvent, opts: MetricEventOptions<EventType>) {
    super(type, opts);
    this.start = opts.start;
    this.end = opts.end;
    this.duration = opts.duration || this.end - this.start;
  }
}

interface AudioTrackSelectedEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  audioTrack: AudioTrack;
}

export class AudioTrackSelectedEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public audioTrack: AudioTrack;

  constructor(opts: AudioTrackSelectedEventOptions<EventType>) {
    super(PlayerEvent.AudioTrackSelected, opts);
    this.audioTrack = opts.audioTrack;
  }
}

interface TextTrackSelectedEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  textTrack: TextTrack;
}

export class TextTrackSelectedEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  // @deprecated please use textTrack
  public subtitles: TextTrack;
  public textTrack: TextTrack;

  constructor(opts: TextTrackSelectedEventOptions<EventType>) {
    super(PlayerEvent.TextTrackSelected, opts);
    this.subtitles = opts.textTrack;
    this.textTrack = opts.textTrack;
  }
}

/**
 * @deprecated please use TextTrackSelectedEvent
 */
export class SubtitlesSelectedEvent<EventType> extends TextTrackSelectedEvent<EventType> {}

interface PlaybackSpeedChangedEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  playbackSpeed: number;
}

export class PlaybackSpeedChangedEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public playbackSpeed: number;

  constructor(opts: PlaybackSpeedChangedEventOptions<EventType>) {
    super(PlayerEvent.PlaybackSpeedChanged, opts);
    this.playbackSpeed = opts.playbackSpeed;
  }
}

type Metadata = Record<string, string>;
interface TimedMetadataEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  metadata: Metadata;
  playhead: number;
}

export class TimedMetadataEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  public metadata: Metadata;
  public playhead: number;

  constructor(opts: TimedMetadataEventOptions<EventType>) {
    super(PlayerEvent.TimedMetadata, opts);
    this.metadata = opts.metadata;
    this.playhead = opts.playhead;
  }
}

export class MetadataUpdatedEvent extends Event implements PublicMetadataUpdatedEvent {
  public type!: typeof PlayerEvent.MetadataUpdated;

  constructor(public metadata: PublicMetadataUpdatedEvent['metadata']) {
    super(PlayerEvent.MetadataUpdated);
  }
}
